/**
 * thunderSound.ts — صوت شرارة البرق: **عيّنة مسجّلة** مع مُولَّدٍ احتياطي.
 *
 * ── العيّنة (طلب 2026-09-16) ────────────────────────────────────────────────
 * `public/sounds/lightning-spark.mp3` — شرارة كهربائية اختارها العميل. قُصّت إلى
 * 0.75 ثانية (فرقعة + ذيل 0.35ث ثم صمت، فالباقي كان صمتاً محضاً يؤخّر فكّ الترميز)
 * بحجم 13 ك.ب. تُنزَّل **مرّة واحدة عند أوّل تشغيل للصوت** لا مع الصفحة: من يُبقي
 * المفتاح مطفأً — وهو الافتراضي — لا يدفع بايتاً.
 *
 * ── ولماذا بقي المُولَّد ─────────────────────────────────────────────────────
 * احتياطياً لا بديلاً: إن فشل تنزيل العيّنة أو فكّ ترميزها (شبكة، أو صيغة يرفضها
 * متصفّح قديم) يبقى صوتٌ بدل صمتٍ لا يفسّره شيء. وهو ضجيج بُنّي بمرشّح تمرير منخفض
 * وغلاف سعة — فيزياء الرعد نفسها: فرقعة عالية للقريب وهدير منخفض للبعيد.
 *
 * ── تنبيه رخصي ─────────────────────────────────────────────────────────────
 * العيّنة من مكتبة مؤثّرات أرسلها العميل، ولم أتحقّق من رخصتها التجارية. إن تبيّن
 * أنها غير مرخّصة فالاستبدال سطرٌ واحد (‏`SPARK_URL`) أو حذف الملفّ — والمُولَّد أدناه
 * يتولّى الصوت وحده كما كان.
 *
 * ── سياسة التشغيل التلقائي ─────────────────────────────────────────────────
 * المتصفّحات تمنع أي صوت قبل لمسةٍ من المستخدم. لذلك الصوت **مُطفأ افتراضياً** ويُفتح
 * من مفتاح في قائمة التراكبات — ونقرة ذلك المفتاح نفسها هي اللمسة التي تفتح السياق
 * (`unlockThunder`). بلا ذلك يُنشأ السياق «معلّقاً» ولا يُسمع شيء بلا أي خطأ.
 *
 * ── لماذا سقف للتكرار ───────────────────────────────────────────────────────
 * عاصفة نشطة تُطلق عشرات الومضات في الثانية على الشاشة. صوتٌ لكل ومضة ضجيجٌ متّصل
 * لا رعد. فالطبقة تجمع ومضات الإطار في «حدث» واحد، وهنا سقفٌ بين رعدتين.
 */

let ctx: AudioContext | null = null;
let noise: AudioBuffer | null = null;
let lastPlayMs = 0;

/** عيّنة الشرارة بعد فكّ الترميز، أو `false` إن فشل تحميلها فلا يُعاد المحاولة أبداً. */
let spark: AudioBuffer | false | null = null;
let sparkLoading: Promise<void> | null = null;

const SPARK_URL = `${import.meta.env.BASE_URL}sounds/lightning-spark.mp3`;

/**
 * يُنزّل العيّنة ويفكّ ترميزها مرّةً واحدة.
 *
 * `decodeAudioData` بنمط الوعد لا بردّ النداء: النمط القديم لا يرفض في Safari بل
 * يصمت، فيبقى `sparkLoading` معلّقاً إلى الأبد ولا يعمل الاحتياطي.
 */
function loadSpark(ac: AudioContext): void {
    if (spark !== null || sparkLoading) return;
    sparkLoading = fetch(SPARK_URL)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((b) => ac.decodeAudioData(b))
        .then((buf) => { spark = buf; })
        .catch(() => { spark = false; });
}

/**
 * أدنى فاصل بين شرارتين (مللي). قُصّر من 1400 إلى 900 مع العيّنة: الرعدة المولَّدة كانت
 * تمتدّ ثلاث ثوانٍ فتتداخل، والشرارة 0.75 — ففاصلٌ أطول منها بقليل يُبقي كلّ شرارة
 * مستقلّة مسموعة، وأقصر منه يتحوّل الصوت إلى طقطقةٍ متّصلة في عاصفة كثيفة.
 */
const MIN_GAP_MS = 900;

function context(): AudioContext | null {
    if (ctx) return ctx;
    const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try { ctx = new Ctor(); } catch { return null; }
    return ctx;
}

/**
 * ضجيج بُنّي (تكامل ضجيج أبيض) لثلاث ثوانٍ — يُبنى مرّة ويُعاد استعماله.
 * البُنّي لا الأبيض: طاقته تتركّز في الترددات المنخفضة فيُسمع «هديراً» لا «هسهسة».
 */
function brownNoise(ac: AudioContext): AudioBuffer {
    if (noise) return noise;
    const len = Math.floor(ac.sampleRate * 3);
    const buf = ac.createBuffer(1, len, ac.sampleRate);
    const data = buf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
        const white = Math.random() * 2 - 1;
        last = (last + 0.02 * white) / 1.02;
        data[i] = last * 3.5;
    }
    noise = buf;
    return buf;
}

/** يُستدعى من نقرة المستخدم على المفتاح — يفتح السياق المعلّق وفق سياسة المتصفّح. */
export function unlockThunder(): void {
    const ac = context();
    if (!ac) return;
    if (ac.state === 'suspended') void ac.resume();
    // التنزيل يبدأ هنا لا عند أوّل ومضة: بلا ذلك تمرّ أوّل شرارة صامتةً بينما الملفّ
    // في الطريق، ثم تُسمع الثانية — فيبدو المفتاح كأنه لا يعمل.
    loadSpark(ac);
    // Safari على iOS لا يفتح السياق فعلاً إلا بصوتٍ يبدأ **داخل اللمسة نفسها** — عيّنة
    // صامتة واحدة تكفي، وبدونها يبقى الرعد صامتاً على الآيفون رغم تشغيل المفتاح.
    try {
        const src = ac.createBufferSource();
        src.buffer = ac.createBuffer(1, 1, 22050);
        src.connect(ac.destination);
        src.start(0);
    } catch { /* لا شيء: المحاولة التالية عند اللمسة القادمة */ }
}

/**
 * شرارةٌ واحدة.
 *
 * @param intensity 0..1 — من كثافة الومضات الظاهرة: يرفع الصوت ويُقرّب الشرارة.
 *   ضعيف = شرارة بعيدة خافتة.
 */
export function playThunder(intensity: number): void {
    const now = performance.now();
    if (now - lastPlayMs < MIN_GAP_MS) return;
    const ac = context();
    if (!ac || ac.state !== 'running') return;
    lastPlayMs = now;

    const k = Math.max(0, Math.min(1, intensity));
    const t0 = ac.currentTime;
    if (spark === null) loadSpark(ac);
    if (spark) { playSpark(ac, spark, k, t0); return; }
    // مدّة بين 1.6 و3 ثوانٍ مع عشوائية خفيفة — رعدتان متطابقتان تفضحان التوليد.
    const dur = 1.6 + 1.4 * Math.random();

    const src = ac.createBufferSource();
    src.buffer = brownNoise(ac);

    const lp = ac.createBiquadFilter();
    lp.type = 'lowpass';
    // القريب يحمل تردّدات أعلى (فرقعة)، والبعيد تُصفّيه المسافة فيبقى هديراً.
    lp.frequency.setValueAtTime(260 + 900 * k, t0);
    lp.frequency.exponentialRampToValueAtTime(90, t0 + dur);

    const gain = ac.createGain();
    const peak = 0.25 + 0.55 * k;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.04);             // الفرقعة
    gain.gain.exponentialRampToValueAtTime(peak * 0.45, t0 + 0.35);
    gain.gain.linearRampToValueAtTime(peak * 0.55, t0 + 0.6 + 0.3 * Math.random()); // تدحرج ثانٍ
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    src.connect(lp).connect(gain).connect(ac.destination);
    // بداية عشوائية داخل الضجيج: كل رعدة بنسيجٍ مختلف.
    src.start(t0, Math.random() * 1.2, dur + 0.1);
}

/**
 * تشغيل العيّنة بشدّةٍ ونبرةٍ تتبعان كثافة الومضات.
 *
 * ── لماذا لا تُشغَّل كما هي ──────────────────────────────────────────────────
 * عيّنةٌ واحدة تتكرّر بلا تغيير تُسمع «حلقةً» مصطنعة بعد ثلاث مرّات — وهذا أسوأ من
 * لا صوت. فمرّتان من التنويع:
 *   • **النغمة** (`playbackRate`) ‏±8٪ عشوائياً: كل شرارة بطبقةٍ مختلفة قليلاً.
 *   • **المسافة**: الشرارة البعيدة تُصفّى نغماتها العالية — مرشّح تمرير منخفض عند
 *     3 ك.هرتز للضعيفة يفتح حتى 12 ك.هرتز للقويّة. وهي فيزياء لا زينة: الهواء
 *     يمتصّ العالي أسرع، ولذلك يُفرقع القريب ويهدر البعيد.
 */
function playSpark(ac: AudioContext, buf: AudioBuffer, k: number, t0: number): void {
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 0.92 + 0.16 * Math.random();

    const lp = ac.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3000 + 9000 * k;

    const gain = ac.createGain();
    gain.gain.value = 0.22 + 0.55 * k;

    src.connect(lp).connect(gain).connect(ac.destination);
    src.start(t0);
}
